import React, {useMemo} from 'react';
import {Page} from '../../presenters/pages/index';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {grey} from '@material-ui/core/colors';
import Tooltip from '@material-ui/core/Tooltip';
import type {Edge, Node} from '@xyflow/react';
import * as XY from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {useLocation} from 'react-router-dom';

const {ReactFlow, Background, Controls, MiniMap, MarkerType, useNodesState, useEdgesState} = XY;
const EDGE_ARROW = {type: MarkerType.ArrowClosed};

const HIGHLIGHT = '#000000';
const DIM = '#bdbdbd';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {display: 'flex', flexDirection: 'column'},
        graphContainer: {
            height: '84vh',
            border: `1px solid ${grey[300]}`,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#fff',
        },
    })
);

const useTooltipStyles = makeStyles((theme: Theme) =>
    createStyles({
        tooltip: {
            fontSize: 18,
            lineHeight: 1.8,
            maxWidth: 500
        },
    })
);

const ERR_TO_MESSAGE: Record<string, string> = {
    'ERR-302-DELIVERY_ERROR': 'Impossible to find a delivery date',
    'ERR-409-PPS-CONFLICT': 'No default PPS is defined for the allocation point chosen',
    'ERR-409-BELT-ALLOCATION-CONFLICT': 'Reversible belt products can not be located in a warehouse',
    'ERR-409-HAT-LAVALLIERE-ALLOCATION-CONFLICT': 'Hat & lavalliere products can not be located',
    'ERR-203-NO_STOCK_LOCATION_FOUND': 'No stock location found for the stockpool',
    'ERR-201-PRODUCT_NOT_AVAILABLE_IN_STOCK': 'No stock found in open stock locations that are eligible to the products',
    'ERR-206-BATH_AND_CANDLE_ALLOCATION_SETUP_MISSING': 'No default PPS configured for Candles and Bath & Body product',
};

const INFO_TO_MESSAGE: Record<string, string> = {
    'ALLOCATION_FULFILLED_BY_DG_COMPLIANT_PPS': 'At least one shipping point uses the default DG / PPS to make allocation successful, because DG compliancy or PPS setup was missing to ship',
};

type LevelMap = Record<string, number>;
type OrderMap = Record<string, number>;
type EdgeMarker = XY.EdgeMarker;

/**
 * Custom node that wraps the entire node box with a Material-UI Tooltip
 * when data.err or data.info is provided.
 */
const TooltipNode = (props: XY.NodeProps) => {
    const {data} = props as any;
    const label = data?.label;
    const err = data?.err as string | undefined;
    const info = data?.info as string | undefined;
    const tooltipClasses = useTooltipStyles();

    const body = (
        <div style={{width: '100%', height: '100%'}}>
            <XY.Handle type="target" position={XY.Position.Top}/>
            <div style={{width: '100%', height: '100%'}}>{label}</div>
            <XY.Handle type="source" position={XY.Position.Bottom}/>
        </div>
    );

    if (!err && !info) return body;

    const isError = Boolean(err);
    const message = err || info;
    const icon = isError ? '⚠️' : 'ℹ️';

    return (
        <Tooltip
            arrow
            placement="top"
            classes={{ tooltip: tooltipClasses.tooltip }}
            title={
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, lineHeight: 1 }}>
                        {icon}
                    </div>
                    <div style={{ marginTop: 6 }}>
                        {message}
                    </div>
                </div>
            }
        >
      <span
          style={{
              display: 'block',
              width: '100%',
              height: '100%',
              fontWeight: 'inherit',
              fontSize: 'inherit',
              color: 'inherit',
          }}
      >
        {body}
      </span>
        </Tooltip>
    );
};

function toMarkerObject(m?: EdgeMarker | XY.MarkerType): EdgeMarker {
    if (m && typeof m === 'object') return m as EdgeMarker;
    return {type: MarkerType.ArrowClosed, width: 16, height: 16};
}

/**
 * Grid layout with:
 * - TRUE column alignment (shared column centers across all rows)
 * - TRUE row alignment (shared row centers across all columns)
 * - Each node centered within its (row, col) cell both horizontally & vertically
 */
function layoutByLevel(
    nodes: Node[],
    _edges: Edge[],
    levelOf: LevelMap,
    orderOf: OrderMap = {},
    dir: 'TB' | 'LR' = 'TB',
    levelGap = 180, // vertical spacing between rows
    colGap = 100, // horizontal spacing between columns
    leftPad = 80, // left margin
    topPad = 40 // top margin
) {
    // Node dimensions
    const dims = new Map<string, { w: number; h: number }>();
    nodes.forEach((n) => {
        const w = Number((n.style as any)?.width) || 240;
        const h = Number((n.style as any)?.height) || 80;
        dims.set(n.id, {w, h});
    });

    // Group nodes by row (level)
    const levels = new Map<number, string[]>();
    nodes.forEach((n) => {
        const lvl = levelOf[n.id] ?? 0;
        const arr = levels.get(lvl) ?? [];
        arr.push(n.id);
        levels.set(lvl, arr);
    });

    const sortedLevels = Array.from(levels.keys()).sort((a, b) => a - b);

    // Compute row max height (for consistent row bands)
    const rowMaxH = new Map<number, number>();
    sortedLevels.forEach((lvl) => {
        const ids = levels.get(lvl)!;
        rowMaxH.set(lvl, Math.max(...ids.map((id) => dims.get(id)!.h)));
    });

    // Compute global max width per column (for consistent column bands)
    const colMaxW = new Map<number, number>();
    nodes.forEach((n) => {
        const col = orderOf[n.id] ?? 1;
        const w = dims.get(n.id)!.w;
        colMaxW.set(col, Math.max(colMaxW.get(col) ?? 0, w));
    });

    const cols = Array.from(colMaxW.keys()).sort((a, b) => a - b);

    // Compute column start X positions
    const colStartX = new Map<number, number>();
    let xCursor = leftPad;
    cols.forEach((col) => {
        colStartX.set(col, xCursor);
        xCursor += (colMaxW.get(col) ?? 0) + colGap;
    });

    // Assign positions by cell, centering inside cell (both axes)
    const positions = new Map<string, { x: number; y: number }>();
    let yCursor = topPad;

    sortedLevels.forEach((lvl) => {
        const ids = levels.get(lvl)!;
        const rowH = rowMaxH.get(lvl) ?? 0;

        ids.forEach((id) => {
            const col = orderOf[id] ?? 1;
            const cellX = colStartX.get(col) ?? leftPad;
            const cellW = colMaxW.get(col) ?? 0;

            const {w, h} = dims.get(id)!;

            // Center horizontally within the column cell
            const x = cellX + (cellW - w) / 2;

            // Center vertically within the row cell
            const y = yCursor + (rowH - h) / 2;

            positions.set(id, {x, y});
        });

        yCursor += rowH + levelGap;
    });

    // Apply positions (and rotate if LR)
    return nodes.map((n) => {
        const p = positions.get(n.id) || {x: leftPad, y: topPad};
        return dir === 'TB'
            ? ({...n, position: p} as Node)
            : ({...n, position: {x: p.y, y: p.x}} as Node);
    });
}

const NODES: Node[] = [
    {
        id: 'TITLE',
        data: {label: 'Home Delivery Decision tree'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {
            width: 400,
            height: 50,
            borderRadius: 8,
            padding: 8,
            background: '#111',
            color: '#eee',
            border: '1px solid #555',
            textAlign: 'center',
        },
    },
    {
        id: 'N0000',
        data: {
            label:
                'Start\nprerequisite : All open stock locations\nCheck product type and stock location compliancy\n(dangerous goods, reversible belt, hat & lavallière)',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 700, height: 150, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0001',
        data: {label: 'Check Single product in the basket'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 500, height: 50, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0007',
        data: {label: 'Check Dangerous good'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 360, height: 50, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0006',
        data: {
            label:
                'Check if all products can be allocated in one stock location\n\nOnly warehouse and PPS are taken into consideration',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 680, height: 120, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0002',
        data: {
            label:
                'Single Product\nSort Allocations - type\n\n1 - Warehouse\n2 - PPS In Subzone\n3 - PPS Out Subzone\n4 - C&C In Subzone\n5 - C&C Out Subzone\n6 - Transfer only In Subzone\n7 - Transfer Only Out Subzone',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 360, height: 320, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0008',
        data: {
            label:
                'Single Fulfillment\nFind and Sort Allocations - type\n\n1 - Warehouse\n2 - PPS In Subzone\n3 - PPS Out Subzone',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 360, height: 210, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0004',
        data: {
            label:
                'Multi Fulfillment\nSort Allocations - type\n\n1 - Warehouse\n2 - PPS In Subzone\n3 - PPS Out Subzone\n4 - C&C In Subzone\n5 - C&C Out Subzone\n6 - Transfer only In Subzone\n7 - Transfer Only Out Subzone',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 500, height: 320, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0009',
        data: {
            label:
                'Single Product\nSort Allocations - Quantity - Ranking\n\n1 - Check stock locations with biggest quantity\n2 - if same quantity : Stock location Ranking',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 500, height: 180, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0013',
        data: {
            label:
                'Single Fulfillment\nSort Allocations - Quantity - Ranking\n\n1 - Check stock locations with biggest quantity\n2 - if same quantity : Stock location Ranking',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 500, height: 180, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0011',
        data: {
            label:
                'Multi Fulfillment\nSort Allocations - Quantity - Ranking\n\n1 - Check stock locations with biggest quantity\n2 - if same quantity : Stock location Ranking',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 500, height: 200, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0010',
        data: {label: 'Calculate leadtime according address and conf'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 600, height: 80, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0010-2',
        data: {label: 'Calculate leadtime according address and conf'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 600, height: 80, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0005',
        data: {label: 'Single Fulfillment\nBuild allocation result'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 320, height: 90, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'N0012',
        data: {
            label:
                'Try to group products in fulfillment\n- Separate group for DG\n- Group with and without transfer in one fulfillment if same shipping point\nLimit to ONE transfer per fulfillment',
        },
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 800, height: 150, borderRadius: 10, padding: 10, background: 'white'},
    },
    {
        id: 'SN0012',
        data: {label: 'Single or multiple fulfillment\nBuild allocation result'},
        position: {x: 0, y: 0},
        type: 'default',
        draggable: false,
        style: {width: 420, height: 120, borderRadius: 10, padding: 10, background: 'white'},
    },
];

const EDGES: Edge[] = [
    {id: 'e_start_1', source: 'N0000', target: 'N0001', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {
        id: 'e_1_yes_2',
        source: 'N0001',
        target: 'N0002',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'Yes',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {
        id: 'e_1_no_7',
        source: 'N0001',
        target: 'N0007',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'No',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {
        id: 'e_7_yes_4',
        source: 'N0007',
        target: 'N0004',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'Yes',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {
        id: 'e_7_no_6',
        source: 'N0007',
        target: 'N0006',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'No',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {
        id: 'e_6_no_8',
        source: 'N0006',
        target: 'N0008',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'Yes',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {
        id: 'e_6_no_4',
        source: 'N0006',
        target: 'N0004',
        type: 'smoothstep',
        markerEnd: EDGE_ARROW,
        label: 'No',
        labelStyle: {fontWeight: 700, fontSize: 20, transform: 'translateY(40px)'},
        labelBgStyle: {fill: '#fff', transform: 'translateY(40px)'},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    },
    {id: 'e_2_to_9', source: 'N0002', target: 'N0009', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_8_to_13', source: 'N0008', target: 'N0013', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_4_to_11', source: 'N0004', target: 'N0011', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_9_to_10', source: 'N0009', target: 'N0010', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_13_to_10', source: 'N0013', target: 'N0010', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_10_to_5', source: 'N0010', target: 'N0005', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_11_to_10', source: 'N0011', target: 'N0010-2', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_10_to_0012', source: 'N0010-2', target: 'N0012', type: 'smoothstep', markerEnd: EDGE_ARROW},
    {id: 'e_12_to_s0012', source: 'N0012', target: 'SN0012', type: 'smoothstep', markerEnd: EDGE_ARROW},
];

const LEVELS: LevelMap = {
    TITLE: 0,
    N0000: 1,
    N0001: 2,
    N0002: 3,
    N0007: 3,
    N0006: 4,
    N0004: 5,
    N0008: 5,
    N0009: 6,
    N0013: 6,
    N0011: 6,
    N0010: 7,
    'N0010-2': 7,
    N0005: 8,
    N0012: 8,
    SN0012: 9,
};

const ORDER: OrderMap = {
    TITLE: 2,
    N0000: 2,
    N0001: 2,
    N0007: 3,
    N0006: 2,
    N0002: 1,
    N0008: 2,
    N0004: 3,
    N0009: 1,
    N0013: 2,
    N0011: 3,
    N0010: 1,
    'N0010-2': 3,
    N0005: 1,
    N0012: 2,
    SN0012: 2,
};

function formatLabel(raw: unknown): React.ReactNode {
    if (typeof raw !== 'string') return raw as React.ReactNode;
    const parts = raw.split('\n');
    const first = parts.shift() ?? '';
    return (
        <div>
            <strong>{first}</strong>
            {parts.map((p, i) => (
                <React.Fragment key={i}>
                    <br/>
                    {p}
                </React.Fragment>
            ))}
        </div>
    );
}

function getPathNodesFromQuery(search: string): string[] {
    const qs = new URLSearchParams(search);
    const raw = qs.get('tree') || '';
    const ids = raw.match(/N\d+(?:-\d+)?/g) ?? [];

    const containsN0011 = ids.includes('N0011');

    const normalized = ids.map((t) => {
        if (containsN0011 && t === 'N0010') return 'N0010-2';
        return t;
    });

    return normalized ?? [];
}

function getErrFromQuery(search: string): string | null {
    const qs = new URLSearchParams(search);
    return qs.get('err');
}

function getInfoFromQuery(search: string): string | null {
    const qs = new URLSearchParams(search);
    return qs.get('info');
}

function buildEdgeIndex(edges: Edge[]) {
    const map = new Map<string, string>();
    edges.forEach((e) => {
        map.set(`${e.source}|${e.target}`, e.id);
    });
    return map;
}

export const DecisionGraphPage = () => {
    const classes = useStyles();
    const location = useLocation();

    // Register custom node type
    const nodeTypes = useMemo(() => ({tooltipNode: TooltipNode}), []);

    const laidOutNodes = useMemo(
        () => layoutByLevel(NODES, EDGES, LEVELS, ORDER, 'TB', 150, 250, 0, 100),
        []
    );

    const {styledNodes, styledEdges} = useMemo(() => {
        const seq = getPathNodesFromQuery(location.search);
        const hasPath = seq.length > 0;

        const errCode = getErrFromQuery(location.search);
        const errMsg = errCode ? (ERR_TO_MESSAGE[errCode] ?? 'Bad allocation request (technical error)') : undefined;
        const hasErr = Boolean(errCode);

        const infoCode = getInfoFromQuery(location.search);
        const infoMsg = infoCode ? (INFO_TO_MESSAGE[infoCode] ?? 'Bad message mapping (technical error)') : undefined;
        const hasInfo = Boolean(infoCode);

        const lastNodeId = seq[seq.length - 1];
        const firstNodeId = seq[0]; // <--- Added: targets the first node in the path

        if (!hasPath) {
            return {
                styledNodes: laidOutNodes.map((n) => ({
                    ...n,
                    type: n.id === 'TITLE' ? 'default' : 'tooltipNode',
                    data: {
                        ...(n.data || {}),
                        label: formatLabel((n.data as any)?.label),
                    },
                })),
                styledEdges: EDGES,
            };
        }

        const nodeSet = new Set(seq);
        const edgeIdx = buildEdgeIndex(EDGES);
        const edgeSet = new Set<string>();

        for (let i = 0; i < seq.length - 1; i++) {
            const key = `${seq[i]}|${seq[i + 1]}`;
            const edgeId = edgeIdx.get(key);
            if (edgeId) edgeSet.add(edgeId);
        }

        if (nodeSet.has('N0012') && !hasErr) {
            nodeSet.add('SN0012');
            const e = edgeIdx.get('N0012|SN0012');
            if (e) edgeSet.add(e);
        }

        const nodesOut: Node[] = laidOutNodes.map((n) => {
            if (n.id === 'TITLE') {
                return {
                    ...n,
                    data: {
                        ...(n.data || {}),
                        label: formatLabel((n.data as any)?.label),
                    },
                };
            }

            const isActive = nodeSet.has(n.id);
            const isErrorNode = hasErr && n.id === lastNodeId;
            const isInfoNode = hasInfo && n.id === firstNodeId; // <--- Changed: targets first node instead of last

            const base = (n.style as React.CSSProperties) || {};

            let stateStyle: React.CSSProperties = {
                border: `2px solid ${isActive ? HIGHLIGHT : '#bdbdbd'}`,
                boxShadow: isActive ? `0 0 0 3px rgba(25,118,210,0.15)` : 'none',
                opacity: isActive ? 1 : 0.6,
            };

            if (isErrorNode) {
                stateStyle = {
                    border: '2px solid #d32f2f',
                    background: 'rgba(211, 47, 47, 0.08)',
                    boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.18)',
                    opacity: 1,
                };
            } else if (isInfoNode) {
                stateStyle = {
                    border: '2px solid #1976d2',
                    background: 'rgba(25, 118, 210, 0.08)',
                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.18)',
                    opacity: 1,
                };
            }

            return {
                ...n,
                type: 'tooltipNode',
                data: {
                    ...(n.data || {}),
                    label: formatLabel((n.data as any)?.label),
                    err: isErrorNode ? errMsg : undefined,
                    info: isInfoNode ? infoMsg : undefined,
                },
                style: {
                    ...base,
                    ...stateStyle,
                },
            };
        });

        const edgesOut: Edge[] = EDGES.map((e) => {
            const isActive = edgeSet.has(e.id);
            const baseMarker = toMarkerObject(e.markerEnd as any);

            return {
                ...e,
                animated: isActive,
                markerEnd: {
                    ...baseMarker,
                    color: isActive ? HIGHLIGHT : DIM,
                },
                style: {
                    ...(e.style || {}),
                    stroke: isActive ? HIGHLIGHT : DIM,
                    strokeWidth: isActive ? 3 : 1.2,
                    opacity: isActive ? 1 : 0.45,
                },
                labelStyle: e.label
                    ? {
                        ...(e.labelStyle || {}),
                        fill: isActive ? HIGHLIGHT : '#333',
                        fontWeight: isActive ? 800 : 600,
                    }
                    : undefined,
                labelBgStyle: e.label
                    ? {
                        ...(e.labelBgStyle || {}),
                        fill: isActive ? 'rgba(25,118,210,0.10)' : 'rgba(255,255,255,0.9)',
                    }
                    : undefined,
            };
        });

        return {styledNodes: nodesOut, styledEdges: edgesOut};
    }, [location.search, laidOutNodes]);

    const [nodes, , onNodesChange] = useNodesState(styledNodes);
    const [edges, , onEdgesChange] = useEdgesState(styledEdges);

    return (
        <Page title={'Decision Tree'} descriptionLine1={''} descriptionLine2={''}>
            <div className={classes.root}>
                <style>{`
          .react-flow__node {
            font-size: 20px;
            white-space: pre-line;
          }

          .react-flow__node-default {
            background: white;
            border: 2px solid #bdbdbd;
            border-radius: 10px;
            padding: 8px;
          }

          .react-flow__handle {
            opacity: 0;
            pointer-events: none;
          }
        `}</style>

                <div className={classes.graphContainer}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        fitView
                        fitViewOptions={{padding: 0.2}}
                        minZoom={0.25}
                        proOptions={{hideAttribution: true}}
                        defaultEdgeOptions={{
                            type: 'smoothstep',
                            markerEnd: {type: MarkerType.ArrowClosed, width: 16, height: 16, color: DIM},
                            style: {strokeWidth: 1.5},
                        }}
                    >
                        <MiniMap
                            pannable
                            zoomable
                            maskColor="rgba(0,0,0,0.06)"
                            style={{width: 200, height: 140}}
                            nodeColor={(n) => {
                                if (n.id === 'TITLE') return '#111';
                                return '#e0e0e0';
                            }}
                            nodeStrokeColor={(n) => (n.id === 'TITLE' ? '#111' : '#666')}
                            nodeStrokeWidth={1.5}
                        />
                        <Controls showInteractive={false}/>
                        <Background/>
                    </ReactFlow>
                </div>
            </div>
        </Page>
    );
};
